import { keyframes } from '@emotion/core';
import styled from '@emotion/styled';
import { Button, Select as _Select, Tabs, Typography } from '@material-ui/core';
import { ClassAttributes, Component, createElement, MouseEvent, MouseEventHandler } from 'react';
import { FormattedMessage } from 'react-intl';

import { ProjectFilterKey } from '../../../entity/ProjectFilter';
import ProjectOrderTarget from '../../../entity/ProjectOrderTarget';
import { ProjectForAllOfOrganizationFragment } from '../../../generated/graphql';
import theme from '../../../theme';
import ProjectsList, { ProjectListItem } from '../../component/compound/ProjectsList';
import { SelectItem } from '../../component/compound/Select';
import LoadMoreButton from '../../component/primitive/LoadMoreButton';
import Tab from '../../component/primitive/Tab';
import { SCREEN_LARGE } from '../../constant/pageWidth';
import { SPACING_HUGE, SPACING_REGULAR, SPACING_UNIT } from '../../constant/spacing';
import { gridLayoutContainer, gridLayoutItem } from '../../styleUtility/gridLayout';
import mq from '../../styleUtility/mediaquery';

interface Props {
  organizationId: string;
  notDraftProjects: Array<ProjectForAllOfOrganizationFragment>;
  draftProjects: Array<ProjectForAllOfOrganizationFragment>;
  finalizedProjects: Array<ProjectForAllOfOrganizationFragment>;
  hasDraftProjects: Array<string>;
  notDraftProjectsLoading: boolean;
  draftProjectsLoading: boolean;
  finalizedProjectsLoading: boolean;
  hasMoreNotDraftProjects: boolean;
  hasMoreDraftProjects: boolean;
  hasMoreFinalizedProjects: boolean;
  getOrderedProjectList: (projectOrderTarget: ProjectOrderTarget) => void;
  onClickLoadMoreNotDraftProjectButton: (projectOrderTarget: ProjectOrderTarget) => void;
  onClickLoadMoreDraftProjectButton: (projectOrderTarget: ProjectOrderTarget) => void;
  onClickLoadMoreFinalizedProjectButton: (projectOrderTarget: ProjectOrderTarget) => void;
  onClickNewProjectButton: MouseEventHandler<HTMLElement>;
  onClickAnyDeleteButton: (e: MouseEvent<HTMLElement>, project: { id: string }) => void;
  onChangeOrganization: (organizationId: string, projectOrderTarget: ProjectOrderTarget) => void;
  onMount: ({ props }: { props: Props }, projectOrderTarget: ProjectOrderTarget) => void;
  onUnmount: ({ props }: { props: Props }) => void;
  className?: string;
}

interface State {
  activeTab: ProjectFilterKey;
  projectOrderTarget: ProjectOrderTarget;
  isDraftWithSubmittedAtSort: boolean;
}

const initialState = {
  activeTab: 'notDraft' as ProjectFilterKey,
  projectOrderTarget: 'submittedAt' as ProjectOrderTarget,
  isDraftWithSubmittedAtSort: false,
};

class ProjectsListPage extends Component<ClassAttributes<HTMLElement> & Props, State> {
  state = initialState;

  componentDidMount() {
    this.props.onMount({ props: this.props }, this.state.projectOrderTarget);
  }

  componentWillUnmount() {
    this.props.onUnmount({ props: this.props });
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.organizationId !== this.props.organizationId) {
      this.resetTab();
      this.resetSort();
      this.props.onChangeOrganization(nextProps.organizationId, this.state.projectOrderTarget);
    }
  }

  onChangeTab = async (_: any, value: string) => {
    await this.setState({
      activeTab: value as ProjectFilterKey,
    });
    if (this.state.activeTab === 'draft' && this.state.projectOrderTarget === 'submittedAt') {
      // submittedAt順でdraftタブが選択された場合、強制的にstateをupdatedAtに変更する
      await this.setState({
        projectOrderTarget: 'updatedAt' as ProjectOrderTarget,
        isDraftWithSubmittedAtSort: true,
      });
    }
    if (
      this.state.isDraftWithSubmittedAtSort &&
      (this.state.activeTab === 'notDraft' || this.state.activeTab === 'finalized')
    ) {
      await this.setState({
        projectOrderTarget: 'submittedAt' as ProjectOrderTarget,
        isDraftWithSubmittedAtSort: false,
      });
    }
  };

  resetTab = async () => {
    await this.setState({
      activeTab: initialState.activeTab,
    });
  };

  onChangeSortOrder = async (e: any) => {
    await this.setState({
      projectOrderTarget: e.target.value as ProjectOrderTarget,
    });
    await this.props.getOrderedProjectList(this.state.projectOrderTarget);
  };

  resetSort = () => {
    this.setState({
      projectOrderTarget: initialState.projectOrderTarget,
    });
  };

  render() {
    const {
      notDraftProjects,
      draftProjects,
      hasDraftProjects,
      finalizedProjects,
      notDraftProjectsLoading,
      draftProjectsLoading,
      finalizedProjectsLoading,
      hasMoreNotDraftProjects,
      hasMoreDraftProjects,
      hasMoreFinalizedProjects,
      onClickLoadMoreNotDraftProjectButton,
      onClickLoadMoreDraftProjectButton,
      onClickLoadMoreFinalizedProjectButton,
      onClickAnyDeleteButton,
      onClickNewProjectButton,
      className,
    } = this.props;

    let activeTab: ProjectFilterKey = this.state.activeTab;
    let projectOrderTarget: ProjectOrderTarget = this.state.projectOrderTarget;
    // 初期表示時のための表示項目修正処理
    if (this.state.activeTab === 'notDraft' && notDraftProjects.length === 0 && finalizedProjects.length > 0) {
      activeTab = 'finalized';
      projectOrderTarget = 'submittedAt';
    }
    if (this.state.activeTab === 'notDraft' && notDraftProjects.length === 0 && draftProjects.length > 0) {
      activeTab = 'draft';
      projectOrderTarget = 'updatedAt';
    }
    if (this.state.activeTab === 'draft' && draftProjects.length === 0) {
      activeTab = 'notDraft';
      projectOrderTarget = 'submittedAt';
    }
    if (this.state.activeTab === 'finalized' && finalizedProjects.length > 0) {
      activeTab = 'finalized';
      projectOrderTarget = 'submittedAt';
    }

    const notDraftProjectsItem = notDraftProjects.map((project) => (
      <ProjectListItem
        project={project}
        onClickDeleteButton={(_e) => void 0}
        key={project.id}
        hasDraftProject={hasDraftProjects.some((hasDraft) => hasDraft === project.id)}
        projectOrderTarget={projectOrderTarget}
      />
    ));

    const draftProjectsItem = draftProjects.map((project) => (
      <ProjectListItem
        project={project}
        onClickDeleteButton={(e) => onClickAnyDeleteButton(e, project)}
        key={project.id}
        projectOrderTarget={projectOrderTarget}
      />
    ));

    const finalizedProjectsItem = finalizedProjects.map((project) => (
      <ProjectListItem
        project={project}
        onClickDeleteButton={(e) => onClickAnyDeleteButton(e, project)}
        key={project.id}
        projectOrderTarget={projectOrderTarget}
      />
    ));

    return (
      <Root className={className}>
        <Heading>
          <Title>
            <FormattedMessage id="projects_list_page.title" />
          </Title>
          <StyledButton variant="contained" color="primary" onClick={onClickNewProjectButton}>
            <FormattedMessage id="projects_list_page.create_new_project" />
          </StyledButton>
        </Heading>
        {notDraftProjects.length + draftProjects.length + finalizedProjects.length > 0 ? (
          <TabAndMenu>
            <Tabs value={activeTab} onChange={this.onChangeTab} indicatorColor="primary" textColor="primary">
              <Tab
                label={<FormattedMessage id="projects_list_page.tabs.notDraft" />}
                value="notDraft"
                disabled={notDraftProjects.length === 0}
              />
              <Tab
                label={<FormattedMessage id="projects_list_page.tabs.draft" />}
                value="draft"
                disabled={draftProjects.length === 0}
              />
              <Tab
                label={<FormattedMessage id="projects_list_page.tabs.finalized" />}
                value="finalized"
                disabled={finalizedProjects.length === 0}
              />
            </Tabs>
            <Select name="sortOrderMenu" value={projectOrderTarget} onChange={this.onChangeSortOrder}>
              <SelectItem value="submittedAt" disabled={activeTab === 'draft'}>
                <FormattedMessage id="projects_list_page.order_menu.submitted_at" />
              </SelectItem>
              <SelectItem value="updatedAt">
                <FormattedMessage id="projects_list_page.order_menu.updated_at" />
              </SelectItem>
            </Select>
          </TabAndMenu>
        ) : null}

        {activeTab === 'notDraft' && (
          <TabContainer>
            <ProjectsList
              showEmptyState={notDraftProjects.length === 0 && draftProjects.length === 0}
              loading={notDraftProjects.length === 0 && notDraftProjectsLoading}
            >
              {notDraftProjectsItem}
            </ProjectsList>
            {hasMoreNotDraftProjects && (
              <LoadMore>
                <LoadMoreButton
                  isLoading={notDraftProjectsLoading}
                  onClickLoadMore={() => {
                    onClickLoadMoreNotDraftProjectButton(projectOrderTarget);
                  }}
                >
                  <FormattedMessage id="projects_list_page.load_more" />
                </LoadMoreButton>
              </LoadMore>
            )}
          </TabContainer>
        )}
        {activeTab === 'draft' && (
          <TabContainer>
            <ProjectsList
              showEmptyState={notDraftProjects.length === 0 && draftProjects.length === 0}
              loading={draftProjects.length === 0 && draftProjectsLoading}
            >
              {draftProjectsItem}
            </ProjectsList>
            {hasMoreDraftProjects && (
              <LoadMore>
                <LoadMoreButton
                  isLoading={draftProjectsLoading}
                  onClickLoadMore={() => {
                    onClickLoadMoreDraftProjectButton(projectOrderTarget);
                  }}
                >
                  <FormattedMessage id="projects_list_page.load_more" />
                </LoadMoreButton>
              </LoadMore>
            )}
          </TabContainer>
        )}
        {activeTab === 'finalized' && (
          <TabContainer>
            <ProjectsList
              showEmptyState={finalizedProjects.length === 0 && finalizedProjects.length === 0}
              loading={finalizedProjects.length === 0 && finalizedProjectsLoading}
            >
              {finalizedProjectsItem}
            </ProjectsList>
            {hasMoreFinalizedProjects && (
              <LoadMore>
                <LoadMoreButton
                  isLoading={finalizedProjectsLoading}
                  onClickLoadMore={() => {
                    onClickLoadMoreFinalizedProjectButton(projectOrderTarget);
                  }}
                >
                  <FormattedMessage id="projects_list_page.load_more" />
                </LoadMoreButton>
              </LoadMore>
            )}
          </TabContainer>
        )}
      </Root>
    );
  }
}

const Root = styled('div')({
  width: 'auto',
  maxWidth: SCREEN_LARGE,
  marginTop: SPACING_HUGE,
  marginRight: 'auto',
  marginLeft: 'auto',
  paddingBottom: SPACING_UNIT * 12,
  [mq('sp')]: {
    paddingLeft: SPACING_REGULAR,
    paddingRight: SPACING_REGULAR,
  },
});

const Heading = styled('div')({
  ...gridLayoutContainer({
    justifyContent: 'space-between',
    columns: ['1fr', 'auto'],
    rows: ['auto'],
    areas: [['title', 'button']],
  }),
  marginBottom: SPACING_UNIT * 7,
  [mq('sp')]: {
    ...gridLayoutContainer({
      justifyContent: 'space-between',
      columns: ['100%'],
      rows: ['auto', '8px', 'auto'],
      areas: [['title'], ['.'], ['button']],
    }),
    marginBottom: SPACING_UNIT * 3,
  },
});

const Title = styled(Typography)({
  ...gridLayoutItem('title'),
  fontSize: theme.typography.h5.fontSize,
});

const TabAndMenu = styled('div')({
  ...gridLayoutContainer({
    justifyContent: 'space-between',
    columns: ['auto', 'auto'],
    rows: ['auto'],
    areas: [['tab', 'menu']],
  }),
  marginBottom: SPACING_UNIT * 3,
});

const Select = styled(_Select)({
  fontSize: 13,
  [mq('sp')]: {
    display: 'none',
  },
});

const fadeInUp = keyframes({
  from: {
    opacity: 0,
  },

  to: {
    opacity: 1,
  },
});

const TabContainer = styled('div')({
  animation: `${fadeInUp} .8s both`,
});

export const StyledButton = styled(Button)({
  ...gridLayoutItem('button'),
  whiteSpace: 'nowrap',
  [mq('sp')]: {
    justifySelf: 'end',
  },
});

const LoadMore = styled('div')({
  maxWidth: 320,
  margin: `${SPACING_HUGE}px auto 0`,
});

export default ProjectsListPage;
